import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCopy, FaShare, FaArrowRight, FaClock, FaTag, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '../styles/SharedContent.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function SharedContentPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { shareId } = useParams();

  // Store original meta tags to restore them on unmount (use ref to avoid infinite loops)
  const originalMetaTagsRef = useRef(null);

  // Update meta tags for social media sharing
  useEffect(() => {
    if (content) {
      // Save original meta tags only once (if not already saved)
      if (!originalMetaTagsRef.current) {
        originalMetaTagsRef.current = {
          title: document.title,
          description: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
          canonical: document.querySelector('link[rel="canonical"]')?.getAttribute('href') || ''
        };
      }
      
      // Update document title
      document.title = `${content.title || 'Untitled'} | Sighed Kick`;
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', content.description || 'Shared content from Sighed Kick');
      
      // Add or update canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', window.location.href);
      
      // Create or update Open Graph meta tags
      const metaTags = [
        { property: 'og:title', content: content.title || 'Shared Content | Sighed Kick' },
        { property: 'og:description', content: content.description || 'Shared content from Sighed Kick' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: window.location.href },
        { property: 'og:image', content: `${window.location.origin}/images/logo192.png` },
        { property: 'og:site_name', content: 'Sighed Kick' },
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: content.title || 'Shared Content | Sighed Kick' },
        { name: 'twitter:description', content: content.description || 'Shared content from Sighed Kick' },
        { name: 'twitter:image', content: `${window.location.origin}/images/logo192.png` }
      ];
      
      // Add or update meta tags
      metaTags.forEach(({ property, name, content }) => {
        let meta;
        if (property) {
          meta = document.querySelector(`meta[property="${property}"]`);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', property);
            document.head.appendChild(meta);
          }
        } else if (name) {
          meta = document.querySelector(`meta[name="${name}"]`);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', name);
            document.head.appendChild(meta);
          }
        }
        meta.setAttribute('content', content);
      });
      
      // Add JSON-LD structured data
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        'name': content.title || 'Shared Content',
        'description': content.description || 'Shared content from Sighed Kick',
        'dateCreated': content.created_at,
        'url': window.location.href,
        'image': `${window.location.origin}/images/logo192.png`,
        'publisher': {
          '@type': 'Organization',
          'name': 'Sighed Kick',
          'logo': {
            '@type': 'ImageObject',
            'url': `${window.location.origin}/images/logo192.png`
          }
        }
      };
      
      scriptTag.textContent = JSON.stringify(structuredData);
    }
    
    // Cleanup function to restore original meta tags
    return () => {
      const originalMetaTags = originalMetaTagsRef.current;
      if (originalMetaTags) {
        if (originalMetaTags.title) {
          document.title = originalMetaTags.title;
        }
        
        // Remove Open Graph and Twitter meta tags
        const ogTags = document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]');
        ogTags.forEach(tag => tag.remove());
        
        // Restore original description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription && originalMetaTags.description) {
          metaDescription.setAttribute('content', originalMetaTags.description);
        }
        
        // Restore original canonical link or remove it
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
          if (originalMetaTags.canonical) {
            canonicalLink.setAttribute('href', originalMetaTags.canonical);
          } else {
            canonicalLink.remove();
          }
        }
        
        // Remove JSON-LD structured data
        const scriptTag = document.querySelector('script[type="application/ld+json"]');
        if (scriptTag) {
          scriptTag.remove();
        }
      }
    };
  }, [content]);

  useEffect(() => {
    const fetchSharedContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/shared-content/${shareId}/`);
        if (!response.ok) {
          if (response.status === 410) {
            throw new Error('This share link has expired');
          }
          if (response.status === 404) {
            throw new Error('This content no longer exists');
          }
          throw new Error('Failed to load content');
        }
        
        // Parse JSON with error handling
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          throw new Error('Failed to parse server response');
        }
        
        setContent(data);
      } catch (error) {
        setError(error.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedContent();
  }, [shareId]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopyLink = async () => {
    try {
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/#/share/${shareId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link to clipboard:', error);
      toast.error('Failed to copy link to clipboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleString();
    } catch (error) {
      return null;
    }
  };

  if (loading) return (
    <div className="loading">
      <div className="loading-spinner"></div>
      Loading...
    </div>
  );
  
  if (error) return (
    <div className="error">
      <div className="error-icon">⚠️</div>
      {error}
    </div>
  );
  
  if (!content) return (
    <div className="not-found">
      <div className="not-found-icon">🔍</div>
      Content not found
    </div>
  );

  return (
    <div className="shared-content-page">
      <div className="brand-header">
        <Link to="/" className="brand-link">
          <img src="/images/logo48.png" alt="Sighed Kick Logo" className="brand-logo" />
          <h1>Sighed Kick</h1>
        </Link>
      </div>
      
      <div className="shared-content-card">
        <div className="content-header">
          <h1>{content.title || 'Untitled'}</h1>
          <div className="content-actions">
            <button
              className="icon-button"
              onClick={() => handleCopy(content.content)}
              title="Copy prompt"
            >
              <FaCopy /> Copy Prompt
            </button>
            <button
              className="icon-button"
              onClick={handleCopyLink}
              title="Copy link"
            >
              <FaShare /> Share
            </button>
          </div>
        </div>
        
        <div className="content-metadata">
          {content.content_type && (
            <div className="metadata-item">
              <FaTag />
              <span>{content.content_type.name}</span>
            </div>
          )}
          <div className="metadata-item">
            <FaClock />
            <span>Created {new Date(content.created_at).toLocaleDateString()}</span>
          </div>
          {content.share_metadata && (
            <>
              <div className="metadata-item">
                <FaEye />
                <span>{content.share_metadata.view_count} views</span>
              </div>
              {content.share_metadata.expires_at && formatDate(content.share_metadata.expires_at) && (
                <div className="metadata-item">
                  <FaClock />
                  <span>Expires {formatDate(content.share_metadata.expires_at)}</span>
                </div>
              )}
            </>
          )}
        </div>
        
        {content.description && (
          <div className="description">
            <h3>Description</h3>
            {content.description}
          </div>
        )}
        
        <div className="content-section">
          <h3>Content</h3>
          <div className="content-body-wrapper">
            <div className="content-body">
              {/* The backend already replaces the content with processed_content in the API response */}
              {content.content}
            </div>
            <button
              className="copy-button"
              onClick={() => handleCopy(content.content)}
              title="Copy content"
            >
              <FaCopy /> Copy
            </button>
          </div>
        </div>

        <div className="upsell-section">
          <div className="upsell-content">
            <div className="upsell-header">
              <img src="/images/logo48.png" alt="Sighed Kick Logo" className="upsell-logo" />
              <h2>Sighed Kick</h2>
            </div>
            <p className="upsell-tagline">The smart way to manage your AI prompts</p>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">📝</div>
                <h3>Organize</h3>
                <p>Create collections and categories for your prompts</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔄</div>
                <h3>Version Control</h3>
                <p>Track changes and maintain prompt history</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👥</div>
                <h3>Collaborate</h3>
                <p>Share prompts with your team</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔒</div>
                <h3>Secure</h3>
                <p>Keep your prompts safe and private</p>
              </div>
            </div>
            <div className="upsell-actions">
              <Link to="/signup" className="cta-button">
                Get Started Free <FaArrowRight />
              </Link>
              <Link to="/login" className="secondary-link">
                Already have an account? Log in
              </Link>
            </div>
          </div>
        </div>
      </div>

      <footer className="brand-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/images/logo48.png" alt="Sighed Kick Logo" className="footer-logo" />
            <h3>Sighed Kick</h3>
            <p>Your AI prompt management solution</p>
          </div>
          <div className="footer-links">
            <div className="footer-section">
              <h4>Product</h4>
              <Link to="/features">Features</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/docs">Documentation</Link>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <Link to="/about">About</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/security">Security</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Sighed Kick. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default SharedContentPage; 